import { useState, useEffect, useMemo, useRef } from "react";
import { 
  Plus, Search, ArrowUpDown, FileText, MoreHorizontal, 
  Trash2, Edit, Eye, Send, Download, Copy, DollarSign, 
  XCircle, FileCheck, Bell, Check, ChevronDown, Archive, RotateCcw, Loader2
} from "lucide-react";
import { invoiceService } from "../../services/invoice.service";
import { showSmtpWarning } from "../../utils/smtpValidation";
import { useToast } from "../../hooks/useToast";
import { useConfirm } from "../../hooks/useConfirm";
import { useSync } from "../../hooks/useSync";
import Toast from "../../ui/Toast";
import ConfirmDialog from "../../ui/ConfirmDialog";
import PortalDropdown from "../../ui/PortalDropdown";
import DropdownMenu from "../../ui/DropdownMenu";
import { formatCurrency } from "../../utils/formatCurrency";

export default function InvoiceListPage({ onNavigateToEditor, refreshTrigger = 0, onboardingContext, onNavigate }) {
  // State
  const [invoices, setInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]); // For counting purposes (non-archived)
  const [archivedInvoices, setArchivedInvoices] = useState([]); // For archived count
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Sync status
  const { status: syncStatus } = useSync();
  const isSyncing = syncStatus === 'syncing';
  
  // Toast notifications
  const { toast, hideToast, success, error: showError, info } = useToast();
  
  // Confirmation dialogs
  const { dialog, confirm } = useConfirm();
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [archiveFilter, setArchiveFilter] = useState(false); // false = active, true = archived
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortOrder, setSortOrder] = useState("DESC");

  // Row actions
  const [hoveredRow, setHoveredRow] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Sort dropdown
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortButtonRef = useRef(null);
  const [sortButtonRect, setSortButtonRect] = useState(null);

  /* ═══════════════════════════════════════════════════════════
     LOAD INVOICES
     ═══════════════════════════════════════════════════════════ */
  const loadInvoices = async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('[InvoiceList] Loading invoices - statusFilter:', statusFilter, 'archiveFilter:', archiveFilter);
      
      // Load all non-archived invoices for counting
      const allData = await invoiceService.list({
        search: searchQuery,
        sortBy,
        sortOrder,
        archived: false,
      });
      setAllInvoices(allData);
      console.log('[InvoiceList] Loaded', allData.length, 'non-archived invoices for counting');

      // Load archived invoices for archived count
      const archivedData = await invoiceService.list({
        search: searchQuery,
        sortBy,
        sortOrder,
        archived: true,
      });
      setArchivedInvoices(archivedData);
      console.log('[InvoiceList] Loaded', archivedData.length, 'archived invoices');

      // Load filtered invoices for display
      const filters = {
        status: archiveFilter ? "all" : statusFilter, // When viewing archived, show all statuses
        search: searchQuery,
        sortBy,
        sortOrder,
        archived: archiveFilter,
      };

      console.log('[InvoiceList] Loading filtered invoices with:', JSON.stringify(filters));
      const filteredData = await invoiceService.list(filters);
      console.log('[InvoiceList] Received', filteredData.length, 'filtered invoices');
      
      // Enrich invoices with client names if missing
      const enrichedData = await Promise.all(
        filteredData.map(async (invoice) => {
          // If invoice has client_id but no client_name, fetch it
          if (invoice.client_id && !invoice.client_name) {
            try {
              const { clientService } = await import('../../services/client.service');
              const client = await clientService.get(invoice.client_id);
              if (client) {
                return {
                  ...invoice,
                  client_name: client.name,
                };
              }
            } catch (err) {
              console.warn('[InvoiceList] Failed to fetch client:', err);
            }
          }
          return invoice;
        })
      );
      
      console.log('[InvoiceList] Setting invoices state with', enrichedData.length, 'invoices');
      setInvoices(enrichedData);
    } catch (err) {
      console.error("Failed to load invoices:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Load on mount and when filters change OR when refreshTrigger changes
  useEffect(() => {
    loadInvoices();
  }, [statusFilter, archiveFilter, searchQuery, sortBy, sortOrder, refreshTrigger]);

  /* ═══════════════════════════════════════════════════════════
     CREATE NEW INVOICE
     ═══════════════════════════════════════════════════════════ */
  const handleCreateInvoice = async () => {
    // Navigate to editor without creating invoice yet
    // The editor will create the invoice when user enters meaningful data
    onNavigateToEditor(null); // null means new invoice mode
  };

  /* ═══════════════════════════════════════════════════════════
     DELETE DRAFT INVOICE
     ═══════════════════════════════════════════════════════════ */
  const handleDeleteDraft = async (invoice, e) => {
    e?.stopPropagation(); // Prevent row click

    if (invoice.status !== "draft") {
      return; // Should never happen due to UI guards
    }

    const confirmed = await confirm({
      title: "Delete Draft Invoice?",
      message: `Invoice ${invoice.invoice_number} will be permanently deleted. This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (!confirmed) return;

    setDeletingId(invoice.id);

    try {
      await invoiceService.executeAction(invoice.id, "delete");
      
      // Show success feedback
      success(`Invoice ${invoice.invoice_number} deleted`);
      
      // Refresh list
      await loadInvoices();
      
      setOpenMenuId(null);
    } catch (err) {
      console.error("Failed to delete invoice:", err);
      showError(err.message || "Failed to delete invoice");
    } finally {
      setDeletingId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     PREVIEW INVOICE
     ═══════════════════════════════════════════════════════════ */
  const handlePreview = (invoice, e) => {
    e?.stopPropagation();
    // Navigate to editor and trigger preview modal
    onNavigateToEditor(invoice.id, { action: 'preview' });
    setOpenMenuId(null);
  };

  /* ═══════════════════════════════════════════════════════════
     DOWNLOAD PDF
     ═══════════════════════════════════════════════════════════ */
  const handleDownload = async (invoice, e) => {
    e?.stopPropagation();

    if (invoice.status === "draft") {
      showError("Please send the invoice before downloading");
      setOpenMenuId(null);
      return;
    }

    // Navigate to editor and trigger download modal
    onNavigateToEditor(invoice.id, { action: 'download' });
    setOpenMenuId(null);
  };

  /* ═══════════════════════════════════════════════════════════
     SEND INVOICE
     ═══════════════════════════════════════════════════════════ */
  const handleSend = async (invoice, e) => {
    e?.stopPropagation();

    if (invoice.status !== "draft") {
      showError("Only draft invoices can be sent");
      setOpenMenuId(null);
      return;
    }

    // 🔥 SMTP VALIDATION FIRST
    const canProceed = await showSmtpWarning(showError, confirm, () => {
      // Navigate to settings
      if (onNavigate) {
        onNavigate("settings");
      }
    });

    if (!canProceed) {
      setOpenMenuId(null);
      return; // Don't proceed if SMTP is not configured
    }

    const confirmed = await confirm({
      title: "Send Invoice?",
      message: `Invoice ${invoice.invoice_number} will be marked as sent. Line items and tax will be locked.`,
      confirmText: "Send Invoice",
      cancelText: "Cancel",
      variant: "primary",
    });

    if (!confirmed) {
      setOpenMenuId(null);
      return;
    }

    try {
      await invoiceService.executeAction(invoice.id, "send");
      success(`Invoice ${invoice.invoice_number} sent`);
      await loadInvoices();
    } catch (err) {
      showError(err.message || "Failed to send invoice");
    } finally {
      setOpenMenuId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     DUPLICATE INVOICE
     ═══════════════════════════════════════════════════════════ */
  const handleDuplicate = async (invoice, e) => {
    e?.stopPropagation();

    try {
      info("Duplicating invoice...");
      const result = await invoiceService.executeAction(invoice.id, "duplicate");
      success(`Invoice duplicated as ${result.invoice_number}`);
      await loadInvoices();
    } catch (err) {
      showError(err.message || "Failed to duplicate invoice");
    } finally {
      setOpenMenuId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     COPY INVOICE LINK
     ═══════════════════════════════════════════════════════════ */
  const handleCopyLink = async (invoice, e) => {
    e?.stopPropagation();

    try {
      // Generate shareable link (placeholder - implement actual link generation)
      const link = `zylobill://invoice/${invoice.id}`;
      await navigator.clipboard.writeText(link);
      success("Invoice link copied to clipboard");
    } catch (err) {
      showError("Failed to copy link");
    } finally {
      setOpenMenuId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     MARK AS PAID
     ═══════════════════════════════════════════════════════════ */
  const handleMarkAsPaid = async (invoice, e) => {
    e?.stopPropagation();

    const confirmed = await confirm({
      title: "Mark as Paid?",
      message: `Invoice ${invoice.invoice_number} will be marked as fully paid.`,
      confirmText: "Mark as Paid",
      cancelText: "Cancel",
      variant: "primary",
    });

    if (!confirmed) {
      setOpenMenuId(null);
      return;
    }

    try {
      await invoiceService.executeAction(invoice.id, "markPaid", {
        payment_date: new Date().toISOString().split('T')[0],
        mode: "other",
        notes: "Marked as paid from invoice list",
      });

      success(`Invoice ${invoice.invoice_number} marked as paid`);
      await loadInvoices();
    } catch (err) {
      showError(err.message || "Failed to mark as paid");
    } finally {
      setOpenMenuId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     RECORD PAYMENT
     ═══════════════════════════════════════════════════════════ */
  const handleRecordPayment = (invoice, e) => {
    e?.stopPropagation();
    // Navigate to invoice editor and auto-scroll to payment section
    onNavigateToEditor(invoice.id, { action: 'recordPayment' });
    setOpenMenuId(null);
  };

  /* ═══════════════════════════════════════════════════════════
     SEND REMINDER
     ═══════════════════════════════════════════════════════════ */
  const handleSendReminder = async (invoice, e) => {
    e?.stopPropagation();

    try {
      info("Sending payment reminder...");
      await invoiceService.executeAction(invoice.id, "sendReminder");
      success(`Reminder sent for invoice ${invoice.invoice_number}`);
    } catch (err) {
      showError(err.message || "Failed to send reminder");
    } finally {
      setOpenMenuId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     CANCEL INVOICE
     ═══════════════════════════════════════════════════════════ */
  const handleCancel = async (invoice, e) => {
    e?.stopPropagation();

    const confirmed = await confirm({
      title: "Cancel Invoice?",
      message: `Invoice ${invoice.invoice_number} will be marked as cancelled. This action cannot be undone.`,
      confirmText: "Cancel Invoice",
      cancelText: "Keep Invoice",
      variant: "danger",
    });

    if (!confirmed) {
      setOpenMenuId(null);
      return;
    }

    try {
      await invoiceService.executeAction(invoice.id, "cancel");
      success(`Invoice ${invoice.invoice_number} cancelled`);
      await loadInvoices();
    } catch (err) {
      showError(err.message || "Failed to cancel invoice");
    } finally {
      setOpenMenuId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     ARCHIVE INVOICE
     ═══════════════════════════════════════════════════════════ */
  const handleArchive = async (invoice, e) => {
    e?.stopPropagation();

    setDeletingId(invoice.id);

    try {
      await invoiceService.archive(invoice.id);
      success(`Invoice ${invoice.invoice_number} archived`);
      await loadInvoices();
      setOpenMenuId(null);
    } catch (err) {
      console.error("Failed to archive invoice:", err);
      showError(`Failed to archive invoice: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     UNARCHIVE INVOICE
     ═══════════════════════════════════════════════════════════ */
  const handleUnarchive = async (invoice, e) => {
    e?.stopPropagation();

    setDeletingId(invoice.id);

    try {
      await invoiceService.unarchive(invoice.id);
      success(`Invoice ${invoice.invoice_number} restored`);
      await loadInvoices();
      setOpenMenuId(null);
    } catch (err) {
      console.error("Failed to unarchive invoice:", err);
      showError(`Failed to restore invoice: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     DELETE ARCHIVED INVOICE (PERMANENT)
     ═══════════════════════════════════════════════════════════ */
  const handleDeleteArchived = async (invoice, e) => {
    e?.stopPropagation();

    const confirmed = await confirm({
      title: "Permanently Delete Invoice?",
      message: `Invoice ${invoice.invoice_number} will be permanently deleted. This action cannot be undone.`,
      confirmText: "Delete Permanently",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (!confirmed) return;

    setDeletingId(invoice.id);

    try {
      await invoiceService.deleteArchived(invoice.id);
      success(`Invoice ${invoice.invoice_number} permanently deleted`);
      await loadInvoices();
      setOpenMenuId(null);
    } catch (err) {
      console.error("Failed to delete archived invoice:", err);
      showError(err.message || "Failed to delete invoice");
    } finally {
      setDeletingId(null);
    }
  };

  /* ═══════════════════════════════════════════════════════════
     STATUS COUNTS
     ═══════════════════════════════════════════════════════════ */
  const statusCounts = useMemo(() => {
    const counts = {
      all: allInvoices.filter(i => !i.archived_at).length, // Only count non-archived for "all"
      draft: 0,
      sent: 0,
      paid: 0,
      overdue: 0,
      cancelled: 0,
    };

    allInvoices.forEach((invoice) => {
      // Skip archived invoices for status counts
      if (invoice.archived_at) return;

      // Check if invoice is overdue (sent with balance due and past due date)
      const isOverdue = invoice.status === "sent" && 
                       invoice.due_date && 
                       new Date(invoice.due_date) < new Date() && 
                       invoice.balance_due > 0;
      
      if (isOverdue) {
        counts.overdue++;
      } else if (invoice.status === "sent") {
        // Only count as "sent" if NOT overdue
        counts.sent++;
      } else {
        // Count by actual status for other statuses
        if (counts.hasOwnProperty(invoice.status)) {
          counts[invoice.status]++;
        }
      }
    });

    return counts;
  }, [allInvoices]);

  /* ═══════════════════════════════════════════════════════════
     RENDER HELPERS
     ═══════════════════════════════════════════════════════════ */
  const getStatusBadge = (invoice) => {
    let status = invoice.status;
    let styles = {
      draft: "bg-slate-50 text-slate-600 border-slate-200",
      sent: "bg-blue-50 text-blue-700 border-blue-200",
      paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
      partially_paid: "bg-indigo-50 text-indigo-700 border-indigo-200",
      overdue: "bg-amber-50 text-amber-700 border-amber-200",
      cancelled: "bg-gray-50 text-gray-600 border-gray-200",
    };

    // Check if overdue (only for sent invoices with balance due)
    if (status === "sent" && new Date(invoice.due_date) < new Date() && invoice.balance_due > 0) {
      status = "overdue";
    }

    // Display label
    const displayLabel = status === "partially_paid" ? "Partial" : status.charAt(0).toUpperCase() + status.slice(1);

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
          styles[status] || styles.draft
        }`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${
          status === "paid" ? "bg-emerald-500" :
          status === "partially_paid" ? "bg-indigo-500" :
          status === "sent" ? "bg-blue-500" :
          status === "overdue" ? "bg-amber-500" :
          status === "cancelled" ? "bg-gray-400" :
          "bg-slate-400"
        }`}></span>
        {displayLabel}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  /* ═══════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════ */
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openMenuId) setOpenMenuId(null);
    };
    
    if (openMenuId) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [openMenuId]);

  return (
    <div className="h-full flex flex-col bg-slate-50/30">
      {/* Onboarding Context Header */}
      {onboardingContext?.source === 'onboarding' && invoices.length === 0 && (
        <div className="bg-blue-50 border-b border-blue-100 px-4 sm:px-6 lg:px-8 py-3">
          <p className="text-sm text-blue-900">
            <span className="font-medium">Getting started</span>
            <span className="text-blue-600 mx-2">→</span>
            <span>Create your first invoice</span>
          </p>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 sm:gap-3">
              <h1 className="text-xl sm:text-2xl font-semibold text-slate-900">Invoices</h1>
              {isSyncing && (
                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-indigo-600">
                  <Loader2 size={12} className="sm:w-[14px] sm:h-[14px] animate-spin" />
                  <span className="hidden sm:inline">Syncing...</span>
                </div>
              )}
            </div>
            <p className="text-xs sm:text-sm text-slate-600 mt-0.5 sm:mt-1 hidden sm:block">
              Manage and track your invoices
            </p>
          </div>

          {/* Desktop: Button */}
          <button
            onClick={handleCreateInvoice}
            className="hidden lg:flex px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm items-center gap-2 cursor-pointer"
          >
            <Plus size={16} />
            New Invoice
          </button>
        </div>
      </div>

      {/* Mobile FAB (Floating Action Button) */}
      <button
        onClick={handleCreateInvoice}
        className="lg:hidden fixed bottom-6 right-6 z-50 w-14 h-14 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 active:scale-95 transition-all flex items-center justify-center cursor-pointer"
        aria-label="New Invoice"
      >
        <Plus size={24} />
      </button>

      {/* Status Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="
          flex items-center gap-1
          overflow-x-auto scrollbar-hide
          px-4 sm:px-6 lg:px-8
          -mb-px
        ">
          {[
            { key: "all", label: "All", archived: false },
            { key: "draft", label: "Draft", archived: false },
            { key: "sent", label: "Sent", archived: false },
            { key: "paid", label: "Paid", archived: false },
            { key: "overdue", label: "Overdue", archived: false },
            { key: "archived", label: "Archived", archived: true },
          ].map((tab) => {
            const isActive = (tab.archived && archiveFilter) || (!tab.archived && !archiveFilter && statusFilter === tab.key);
            
            return (
              <button
                key={tab.key}
                onClick={() => {
                  if (tab.key === "archived") {
                    setArchiveFilter(true);
                    setStatusFilter("all");
                  } else {
                    setArchiveFilter(false);
                    setStatusFilter(tab.key);
                  }
                }}
                className={`
                  px-3 sm:px-4 py-3 
                  text-xs sm:text-sm font-medium 
                  border-b-2 transition-colors
                  whitespace-nowrap
                  flex-shrink-0
                  cursor-pointer
                  ${
                    isActive
                      ? "border-indigo-600 text-indigo-600"
                      : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                  }
                `}
              >
                {tab.label}
                <span className="ml-1.5 sm:ml-2 text-xs text-slate-500">
                  ({tab.key === "archived" 
                    ? archivedInvoices.length 
                    : statusCounts[tab.key] || 0})
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Search & Sort */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
          {/* Search */}
          <div className="flex-1 max-w-full sm:max-w-md">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <span className="text-xs sm:text-sm text-slate-600 hidden sm:inline">Sort by:</span>
            
            {/* Sort Dropdown Button */}
            <button
              ref={sortButtonRef}
              onClick={() => {
                setSortButtonRect(sortButtonRef.current?.getBoundingClientRect());
                setSortDropdownOpen(!sortDropdownOpen);
              }}
              className="flex-1 sm:flex-none px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors bg-white flex items-center gap-2 min-w-0 sm:min-w-[160px] justify-between cursor-pointer"
            >
              <span className="text-slate-700 truncate">
                {sortBy === "updated_at" && "Last Updated"}
                {sortBy === "issue_date" && "Invoice Date"}
                {sortBy === "total" && "Amount"}
                {sortBy === "invoice_number" && "Invoice Number"}
              </span>
              <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />
            </button>

            {/* Sort Order Toggle */}
            <button
              onClick={() => setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC")}
              className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0 cursor-pointer"
              title={sortOrder === "ASC" ? "Ascending" : "Descending"}
            >
              <ArrowUpDown size={16} />
            </button>
          </div>

          {/* Sort Dropdown Menu */}
          {sortDropdownOpen && (
            <DropdownMenu
              anchorRect={sortButtonRect}
              onClose={() => setSortDropdownOpen(false)}
            >
              <SortOption
                label="Last Updated"
                value="updated_at"
                currentValue={sortBy}
                onClick={() => {
                  setSortBy("updated_at");
                  setSortDropdownOpen(false);
                }}
              />
              <SortOption
                label="Invoice Date"
                value="issue_date"
                currentValue={sortBy}
                onClick={() => {
                  setSortBy("issue_date");
                  setSortDropdownOpen(false);
                }}
              />
              <SortOption
                label="Amount"
                value="total"
                currentValue={sortBy}
                onClick={() => {
                  setSortBy("total");
                  setSortDropdownOpen(false);
                }}
              />
              <SortOption
                label="Invoice Number"
                value="invoice_number"
                currentValue={sortBy}
                onClick={() => {
                  setSortBy("invoice_number");
                  setSortDropdownOpen(false);
                }}
              />
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Invoice List */}
      <div className="flex-1 overflow-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-24 sm:pb-32">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-slate-600">Loading invoices...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-red-600">Error: {error}</div>
          </div>
        ) : invoices.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto">
                <FileText size={24} className="text-slate-400" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-slate-900">
                  {searchQuery || statusFilter !== "all"
                    ? "No invoices found"
                    : "No invoices yet"}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  {searchQuery || statusFilter !== "all"
                    ? "Try adjusting your filters"
                    : "Create your first invoice to get started"}
                </p>
              </div>
              {!searchQuery && statusFilter === "all" && (
                <button
                  onClick={handleCreateInvoice}
                  className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer"
                >
                  Create your first invoice
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Desktop: Table View */}
            <div className="hidden lg:block bg-white rounded-lg border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Invoice
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Client
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Invoice Date
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Total Amount
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Last Updated
                    </th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <InvoiceRow
                      key={invoice.id}
                      invoice={invoice}
                      isHovered={hoveredRow === invoice.id}
                      isMenuOpen={openMenuId === invoice.id}
                      isDeleting={deletingId === invoice.id}
                      onHover={() => setHoveredRow(invoice.id)}
                      onLeave={() => setHoveredRow(null)}
                      onClick={() => onNavigateToEditor(invoice.id)}
                      onMenuToggle={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === invoice.id ? null : invoice.id);
                      }}
                      onMenuClose={() => setOpenMenuId(null)}
                      onEdit={(e) => handlePreview(invoice, e)}
                      onPreview={(e) => handlePreview(invoice, e)}
                      onDownload={(e) => handleDownload(invoice, e)}
                      onSend={(e) => handleSend(invoice, e)}
                      onDuplicate={(e) => handleDuplicate(invoice, e)}
                      onCopyLink={(e) => handleCopyLink(invoice, e)}
                      onMarkAsPaid={(e) => handleMarkAsPaid(invoice, e)}
                      onRecordPayment={(e) => handleRecordPayment(invoice, e)}
                      onSendReminder={(e) => handleSendReminder(invoice, e)}
                      onCancel={(e) => handleCancel(invoice, e)}
                      onDelete={(e) => handleDeleteDraft(invoice, e)}
                      onArchive={(e) => handleArchive(invoice, e)}
                      onUnarchive={(e) => handleUnarchive(invoice, e)}
                      onDeleteArchived={(e) => handleDeleteArchived(invoice, e)}
                      formatDate={formatDate}
                      formatCurrency={formatCurrency}
                      getStatusBadge={getStatusBadge}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: Card View */}
            <div className="lg:hidden space-y-3">
              {invoices.map((invoice) => (
                <InvoiceCard
                  key={invoice.id}
                  invoice={invoice}
                  isMenuOpen={openMenuId === invoice.id}
                  isDeleting={deletingId === invoice.id}
                  onClick={() => onNavigateToEditor(invoice.id)}
                  onMenuToggle={(e) => {
                    e.stopPropagation();
                    setOpenMenuId(openMenuId === invoice.id ? null : invoice.id);
                  }}
                  onMenuClose={() => setOpenMenuId(null)}
                  onEdit={(e) => handlePreview(invoice, e)}
                  onPreview={(e) => handlePreview(invoice, e)}
                  onDownload={(e) => handleDownload(invoice, e)}
                  onSend={(e) => handleSend(invoice, e)}
                  onDuplicate={(e) => handleDuplicate(invoice, e)}
                  onCopyLink={(e) => handleCopyLink(invoice, e)}
                  onMarkAsPaid={(e) => handleMarkAsPaid(invoice, e)}
                  onRecordPayment={(e) => handleRecordPayment(invoice, e)}
                  onSendReminder={(e) => handleSendReminder(invoice, e)}
                  onCancel={(e) => handleCancel(invoice, e)}
                  onDelete={(e) => handleDeleteDraft(invoice, e)}
                  onArchive={(e) => handleArchive(invoice, e)}
                  onUnarchive={(e) => handleUnarchive(invoice, e)}
                  onDeleteArchived={(e) => handleDeleteArchived(invoice, e)}
                  formatDate={formatDate}
                  formatCurrency={formatCurrency}
                  getStatusBadge={getStatusBadge}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={hideToast}
          duration={toast.duration}
        />
      )}

      {/* Confirmation Dialog */}
      {dialog && <ConfirmDialog {...dialog} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   INVOICE ROW COMPONENT
   ═══════════════════════════════════════════════════════════ */
function InvoiceRow({
  invoice,
  isHovered,
  isMenuOpen,
  isDeleting,
  onHover,
  onLeave,
  onClick,
  onMenuToggle,
  onMenuClose,
  onEdit,
  onPreview,
  onDownload,
  onSend,
  onDuplicate,
  onCopyLink,
  onMarkAsPaid,
  onRecordPayment,
  onSendReminder,
  onCancel,
  onDelete,
  onArchive,
  onUnarchive,
  onDeleteArchived,
  formatDate,
  formatCurrency,
  getStatusBadge,
}) {
  const status = invoice.status;
  const isDraft = status === "draft";
  const isSent = status === "sent";
  const isPaid = status === "paid";
  const isPartiallyPaid = status === "partially_paid";
  const isCancelled = status === "cancelled";
  const isOverdue = (isSent || isPartiallyPaid) && new Date(invoice.due_date) < new Date() && invoice.balance_due > 0;
  const isArchived = !!invoice.archived_at;

  // Ref for menu button (trigger)
  const menuButtonRef = useRef(null);

  return (
    <tr
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={onClick}
      className={`border-b border-slate-200 cursor-pointer transition-colors ${
        isHovered ? "bg-slate-50/50" : ""
      } ${isDeleting ? "opacity-50 pointer-events-none" : ""}`}
    >
      {/* Invoice Number */}
      <td className="py-3.5 px-4">
        <div className="font-medium text-sm text-slate-900">
          {invoice.invoice_number}
        </div>
      </td>

      {/* Client */}
      <td className="py-3.5 px-4">
        <div className="text-sm text-slate-900">
          {invoice.client_name || (
            <span className="text-slate-400 italic">No client</span>
          )}
        </div>
      </td>

      {/* Status */}
      <td className="py-3.5 px-4">{getStatusBadge(invoice)}</td>

      {/* Invoice Date */}
      <td className="py-3.5 px-4">
        <div className="text-sm text-slate-700">
          {formatDate(invoice.issue_date)}
        </div>
      </td>

      {/* Total Amount */}
      <td className="py-3.5 px-4 text-right">
        <div className="font-semibold text-sm text-slate-900 tabular-nums">
          {formatCurrency(invoice.total)}
        </div>
      </td>

      {/* Last Updated */}
      <td className="py-3.5 px-4">
        <div className="text-sm text-slate-500">
          {formatDate(invoice.updated_at)}
        </div>
      </td>

      {/* Actions */}
      <td className="py-3.5 px-4 relative">
        <div className="flex items-center justify-end">
          <button
            ref={menuButtonRef}
            onClick={onMenuToggle}
            className={`p-1.5 rounded-md transition-all cursor-pointer ${
              isMenuOpen
                ? "bg-slate-100 text-slate-700 opacity-100"
                : isHovered
                ? "text-slate-400 hover:text-slate-600 hover:bg-slate-100 opacity-100"
                : "text-slate-400 opacity-0 pointer-events-none"
            }`}
            aria-label="More actions"
            aria-expanded={isMenuOpen}
            aria-haspopup="true"
          >
            <MoreHorizontal size={16} />
          </button>
        </div>

        {/* State-Aware Dropdown Menu (Portal) */}
        <PortalDropdown
          isOpen={isMenuOpen}
          onClose={onMenuClose}
          triggerRef={menuButtonRef}
          align="right"
        >
                {/* ARCHIVED INVOICE - Check this FIRST */}
                {isArchived ? (
                  <>
                    <button
                      onClick={onEdit}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Eye size={14} />
                      View invoice
                    </button>
                    <button
                      onClick={onDuplicate}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Copy size={14} />
                      Duplicate invoice
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Restore */}
                    <button
                      onClick={onUnarchive}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <RotateCcw size={14} />
                      Restore invoice
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Danger zone */}
                    <button
                      onClick={onDeleteArchived}
                      disabled={isDeleting}
                      className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <Trash2 size={14} />
                      {isDeleting ? "Deleting..." : "Delete permanently"}
                    </button>
                  </>
                ) : (
                  <>
                {/* DRAFT INVOICE */}
                {isDraft && (
                  <>
                    {/* Primary utilities */}
                    <button
                      onClick={onEdit}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Edit size={14} />
                      Edit invoice
                    </button>
                    <button
                      onClick={onPreview}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Eye size={14} />
                      Preview PDF
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Workflow */}
                    <button
                      onClick={onSend}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Send size={14} />
                      Send invoice
                    </button>
                    <button
                      onClick={onDuplicate}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Copy size={14} />
                      Duplicate invoice
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Data */}
                    <button
                      onClick={onDownload}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Download size={14} />
                      Download PDF
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Danger zone */}
                    <button
                      onClick={onDelete}
                      disabled={isDeleting}
                      className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <Trash2 size={14} />
                      {isDeleting ? "Deleting..." : "Delete invoice"}
                    </button>
                  </>
                )}

                {/* SENT INVOICE */}
                {isSent && !isOverdue && (
                  <>
                    {/* Primary utilities */}
                    <button
                      onClick={onEdit}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Eye size={14} />
                      View invoice
                    </button>
                    <button
                      onClick={onPreview}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <FileCheck size={14} />
                      Preview PDF
                    </button>
                    <button
                      onClick={onDownload}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Download size={14} />
                      Download PDF
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Workflow */}
                    <button
                      onClick={onMarkAsPaid}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <DollarSign size={14} />
                      Mark as paid
                    </button>
                    <button
                      onClick={onRecordPayment}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <DollarSign size={14} />
                      Record payment
                    </button>
                    <button
                      onClick={onDuplicate}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Copy size={14} />
                      Duplicate invoice
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Sharing */}
                    <button
                      onClick={onCopyLink}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Copy size={14} />
                      Copy invoice link
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Archive */}
                    <button
                      onClick={onArchive}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Archive size={14} />
                      Archive invoice
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Danger zone */}
                    <button
                      onClick={onCancel}
                      className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <XCircle size={14} />
                      Cancel invoice
                    </button>
                  </>
                )}

                {/* OVERDUE INVOICE */}
                {isOverdue && (
                  <>
                    {/* Primary utilities */}
                    <button
                      onClick={onEdit}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Eye size={14} />
                      View invoice
                    </button>
                    <button
                      onClick={onPreview}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <FileCheck size={14} />
                      Preview PDF
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Workflow */}
                    <button
                      onClick={onSendReminder}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Bell size={14} />
                      Send reminder
                    </button>
                    <button
                      onClick={onRecordPayment}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <DollarSign size={14} />
                      Record payment
                    </button>
                    <button
                      onClick={onMarkAsPaid}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <DollarSign size={14} />
                      Mark as paid
                    </button>
                    <button
                      onClick={onDuplicate}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Copy size={14} />
                      Duplicate invoice
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Data */}
                    <button
                      onClick={onDownload}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Download size={14} />
                      Download PDF
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Archive */}
                    <button
                      onClick={onArchive}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Archive size={14} />
                      Archive invoice
                    </button>
                  </>
                )}

                {/* PARTIALLY PAID INVOICE (not overdue) */}
                {isPartiallyPaid && !isOverdue && (
                  <>
                    {/* Primary utilities */}
                    <button
                      onClick={onEdit}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Eye size={14} />
                      View invoice
                    </button>
                    <button
                      onClick={onPreview}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <FileCheck size={14} />
                      Preview PDF
                    </button>
                    <button
                      onClick={onDownload}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Download size={14} />
                      Download PDF
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Workflow */}
                    <button
                      onClick={onRecordPayment}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <DollarSign size={14} />
                      Record payment
                    </button>
                    <button
                      onClick={onMarkAsPaid}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <DollarSign size={14} />
                      Mark as paid
                    </button>
                    <button
                      onClick={onDuplicate}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Copy size={14} />
                      Duplicate invoice
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Sharing */}
                    <button
                      onClick={onCopyLink}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Copy size={14} />
                      Copy invoice link
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Archive */}
                    <button
                      onClick={onArchive}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Archive size={14} />
                      Archive invoice
                    </button>
                  </>
                )}

                {/* PAID INVOICE */}
                {isPaid && (
                  <>
                    {/* Primary utilities */}
                    <button
                      onClick={onEdit}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Eye size={14} />
                      View invoice
                    </button>
                    <button
                      onClick={onDownload}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Download size={14} />
                      Download PDF
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Workflow */}
                    <button
                      onClick={onDuplicate}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Copy size={14} />
                      Duplicate invoice
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Data */}
                    <button
                      onClick={onEdit}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <DollarSign size={14} />
                      View payment details
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Archive */}
                    <button
                      onClick={onArchive}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Archive size={14} />
                      Archive invoice
                    </button>
                  </>
                )}

                {/* CANCELLED INVOICE */}
                {isCancelled && (
                  <>
                    <button
                      onClick={onEdit}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Eye size={14} />
                      View invoice
                    </button>
                    <button
                      onClick={onDuplicate}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Copy size={14} />
                      Duplicate invoice
                    </button>

                    {/* Divider */}
                    <div className="my-1 border-t border-slate-100" />

                    {/* Archive */}
                    <button
                      onClick={onArchive}
                      className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                    >
                      <Archive size={14} />
                      Archive invoice
                    </button>
                  </>
                )}
                  </>
                )}
        </PortalDropdown>
      </td>
    </tr>
  );
}

/* ═══════════════════════════════════════════════════════════
   INVOICE CARD COMPONENT (MOBILE)
   ═══════════════════════════════════════════════════════════ */
function InvoiceCard({
  invoice,
  isMenuOpen,
  isDeleting,
  onClick,
  onMenuToggle,
  onMenuClose,
  onEdit,
  onPreview,
  onDownload,
  onSend,
  onDuplicate,
  onCopyLink,
  onMarkAsPaid,
  onRecordPayment,
  onSendReminder,
  onCancel,
  onDelete,
  onArchive,
  onUnarchive,
  onDeleteArchived,
  formatDate,
  formatCurrency,
  getStatusBadge,
}) {
  const status = invoice.status;
  const isDraft = status === "draft";
  const isSent = status === "sent";
  const isPaid = status === "paid";
  const isPartiallyPaid = status === "partially_paid";
  const isCancelled = status === "cancelled";
  const isOverdue = (isSent || isPartiallyPaid) && new Date(invoice.due_date) < new Date() && invoice.balance_due > 0;
  const isArchived = !!invoice.archived_at;

  // Ref for menu button (trigger)
  const menuButtonRef = useRef(null);

  return (
    <div
      onClick={onClick}
      className={`
        bg-white rounded-xl border border-slate-200 p-4 
        cursor-pointer transition-all duration-150
        hover:shadow-md hover:border-slate-300
        active:scale-[0.98]
        ${isDeleting ? "opacity-50 pointer-events-none" : ""}
      `}
    >
      {/* Top Row: Client Name + Status Badge */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-base text-slate-900 truncate">
            {invoice.client_name || (
              <span className="text-slate-400 italic">No client</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {getStatusBadge(invoice)}
          <button
            ref={menuButtonRef}
            onClick={onMenuToggle}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
            aria-label="More actions"
          >
            <MoreHorizontal size={18} />
          </button>
        </div>
      </div>

      {/* Middle Row: Invoice Number + Date */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-sm text-slate-600">
          {invoice.invoice_number}
        </div>
        <div className="text-sm text-slate-500">
          {formatDate(invoice.issue_date)}
        </div>
      </div>

      {/* Bottom Row: Amount */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          Total Amount
        </div>
        <div className="text-lg font-semibold text-slate-900 tabular-nums">
          {formatCurrency(invoice.total)}
        </div>
      </div>

      {/* State-Aware Dropdown Menu (Portal) */}
      <PortalDropdown
        isOpen={isMenuOpen}
        onClose={onMenuClose}
        triggerRef={menuButtonRef}
        align="right"
      >
        {/* ARCHIVED INVOICE - Check this FIRST */}
        {isArchived ? (
          <>
            <button
              onClick={onEdit}
              className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
            >
              <Eye size={14} />
              View invoice
            </button>
            <button
              onClick={onDuplicate}
              className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
            >
              <Copy size={14} />
              Duplicate invoice
            </button>

            {/* Divider */}
            <div className="my-1 border-t border-slate-100" />

            {/* Restore */}
            <button
              onClick={onUnarchive}
              className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
            >
              <RotateCcw size={14} />
              Restore invoice
            </button>

            {/* Divider */}
            <div className="my-1 border-t border-slate-100" />

            {/* Danger zone */}
            <button
              onClick={onDeleteArchived}
              disabled={isDeleting}
              className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 size={14} />
              {isDeleting ? "Deleting..." : "Delete permanently"}
            </button>
          </>
        ) : (
          <>
            {/* DRAFT INVOICE */}
            {isDraft && (
              <>
                <button
                  onClick={onEdit}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Edit size={14} />
                  Edit invoice
                </button>
                <button
                  onClick={onPreview}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Eye size={14} />
                  Preview PDF
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onSend}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Send size={14} />
                  Send invoice
                </button>
                <button
                  onClick={onDuplicate}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Copy size={14} />
                  Duplicate invoice
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onDownload}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Download size={14} />
                  Download PDF
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onDelete}
                  disabled={isDeleting}
                  className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 size={14} />
                  {isDeleting ? "Deleting..." : "Delete invoice"}
                </button>
              </>
            )}

            {/* SENT INVOICE */}
            {isSent && !isOverdue && (
              <>
                <button
                  onClick={onEdit}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Eye size={14} />
                  View invoice
                </button>
                <button
                  onClick={onPreview}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <FileCheck size={14} />
                  Preview PDF
                </button>
                <button
                  onClick={onDownload}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Download size={14} />
                  Download PDF
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onMarkAsPaid}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <DollarSign size={14} />
                  Mark as paid
                </button>
                <button
                  onClick={onRecordPayment}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <DollarSign size={14} />
                  Record payment
                </button>
                <button
                  onClick={onDuplicate}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Copy size={14} />
                  Duplicate invoice
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onCopyLink}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Copy size={14} />
                  Copy invoice link
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onArchive}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Archive size={14} />
                  Archive invoice
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onCancel}
                  className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <XCircle size={14} />
                  Cancel invoice
                </button>
              </>
            )}

            {/* OVERDUE INVOICE */}
            {isOverdue && (
              <>
                <button
                  onClick={onEdit}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Eye size={14} />
                  View invoice
                </button>
                <button
                  onClick={onPreview}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <FileCheck size={14} />
                  Preview PDF
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onSendReminder}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Bell size={14} />
                  Send reminder
                </button>
                <button
                  onClick={onRecordPayment}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <DollarSign size={14} />
                  Record payment
                </button>
                <button
                  onClick={onMarkAsPaid}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <DollarSign size={14} />
                  Mark as paid
                </button>
                <button
                  onClick={onDuplicate}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Copy size={14} />
                  Duplicate invoice
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onDownload}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Download size={14} />
                  Download PDF
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onArchive}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Archive size={14} />
                  Archive invoice
                </button>
              </>
            )}

            {/* PARTIALLY PAID INVOICE (not overdue) */}
            {isPartiallyPaid && !isOverdue && (
              <>
                <button
                  onClick={onEdit}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Eye size={14} />
                  View invoice
                </button>
                <button
                  onClick={onPreview}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <FileCheck size={14} />
                  Preview PDF
                </button>
                <button
                  onClick={onDownload}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Download size={14} />
                  Download PDF
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onRecordPayment}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <DollarSign size={14} />
                  Record payment
                </button>
                <button
                  onClick={onMarkAsPaid}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <DollarSign size={14} />
                  Mark as paid
                </button>
                <button
                  onClick={onDuplicate}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Copy size={14} />
                  Duplicate invoice
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onCopyLink}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Copy size={14} />
                  Copy invoice link
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onArchive}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Archive size={14} />
                  Archive invoice
                </button>
              </>
            )}

            {/* PAID INVOICE */}
            {isPaid && (
              <>
                <button
                  onClick={onEdit}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Eye size={14} />
                  View invoice
                </button>
                <button
                  onClick={onDownload}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Download size={14} />
                  Download PDF
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onDuplicate}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Copy size={14} />
                  Duplicate invoice
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onEdit}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <DollarSign size={14} />
                  View payment details
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onArchive}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Archive size={14} />
                  Archive invoice
                </button>
              </>
            )}

            {/* CANCELLED INVOICE */}
            {isCancelled && (
              <>
                <button
                  onClick={onEdit}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Eye size={14} />
                  View invoice
                </button>
                <button
                  onClick={onDuplicate}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Copy size={14} />
                  Duplicate invoice
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  onClick={onArchive}
                  className="w-full text-left px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2 cursor-pointer"
                >
                  <Archive size={14} />
                  Archive invoice
                </button>
              </>
            )}
          </>
        )}
      </PortalDropdown>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SORT OPTION COMPONENT
   ═══════════════════════════════════════════════════════════ */
function SortOption({ label, value, currentValue, onClick }) {
  const isSelected = value === currentValue;
  
  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between gap-3 cursor-pointer
        ${isSelected 
          ? "bg-slate-50 text-slate-900 font-medium" 
          : "text-slate-700 hover:bg-slate-50"
        }
      `}
    >
      <span>{label}</span>
      {isSelected && (
        <Check size={14} className="text-slate-900" />
      )}
    </button>
  );
}
